import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import { Create, Share, Print, MenuBook } from "@mui/icons-material";
import SEO from "../components/common/SEO";
import StructuredData from "../components/common/StructuredData";
import "./Welcome.css";

const Welcome = () => {
  const navigate = useNavigate();
  

  const features = [
    {
      icon: <Create />,
      title: "Create Patterns",
      description:
        "Design custom stitch charts with our interactive grid editor. Choose from a full palette of knitting symbols and build patterns up to 50x50 stitches.",
    },
    {
      icon: <MenuBook />,
      title: "Written Instructions",
      description:
        "Automatically generate row-by-row written instructions from your chart. Includes RS/WS handling, stitch compression, and copy-to-clipboard support.",
    },
    {
      icon: <Share />,
      title: "Share Patterns",
      description:
        "Submit your patterns to the community gallery where others can browse, search, and export. Or keep your designs private and share via email.",
    },
    {
      icon: <Print />,
      title: "Print Ready",
      description:
        "Export professional PDFs with charts and written instructions, or save as JSON for easy editing. Perfect for printing or digital sharing.",
    },
  ];

  const baseUrl = process.env.REACT_APP_BASE_URL || "https://loopsandpurls.com";

  return (
    <>
      <SEO
        title="Loops & Purls"
        description="Create and share knitting patterns with our free chart maker. Design custom stitch charts up to 50x50, export PDFs and JSON, and browse the pattern gallery — no account required."
        keywords="knitting patterns, free knitting chart maker, stitch chart designer, knitting pattern creator, knit stitch chart, purl stitch, knitting app, crochet charts, pattern gallery, knitting tool"
      />
      <StructuredData
        type="WebApplication"
        data={{
          name: "Loops & Purls",
          description: "Create and share knitting patterns with our free chart maker. Design custom stitch charts up to 50x50, export PDFs and JSON, and browse the pattern gallery — no account required.",
          url: baseUrl || "https://loopsandpurls.com",
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Web",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          featureList: [
            "Create knitting pattern charts",
            "Export to PDF and JSON",
            "Share patterns to gallery",
            "Browse community patterns",
            "No account required",
          ],
          screenshot: baseUrl ? `${baseUrl}/square-logo.png` : "/square-logo.png",
        }}
      />
      <StructuredData
        type="Organization"
        data={{
          name: "Loops & Purls",
          url: baseUrl || "https://loopsandpurls.com",
          logo: baseUrl ? `${baseUrl}/square-logo.png` : "/square-logo.png",
          description: "A modern web application for creating and sharing knitting patterns.",
          sameAs: [],
        }}
      />
      <Container maxWidth="lg" className="welcome-container">
      <Box className="welcome-header">
        <Typography
          variant="h1"
          component="h1"
          gutterBottom
          className="welcome-title"
        >
          Loops & Purls
        </Typography>
        <Typography
          variant="h5"
          component="h2"
          color="text.secondary"
          className="text-center mb-4"
        >
          Create and share knitting patterns — no login required
        </Typography>

        {/* Auth removed */}

        <Box className="welcome-button-group">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/chart-maker")}
            className="welcome-button"
          >
            Start Creating
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/gallery")}
            className="welcome-button"
          >
            Browse Gallery
          </Button>
        </Box>
      </Box>

      <div className="row welcome-features-grid">
        {features.map((feature, index) => (
          <div className="col-12 col-md-6" key={index}>
            <Card className="welcome-feature-card">
              <CardContent>
                <Box className="welcome-feature-icon">{feature.icon}</Box>
                <Typography variant="h6" component="h3" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Paper className="welcome-cta-paper">
        <Typography variant="h4" component="h3" gutterBottom>
          Ready to Get Started?
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          className="welcome-cta-description mb-3"
        >
          No account required. Download PDF/JSON to save anywhere or email it.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/chart-maker")}
        >
          Create Your First Pattern
        </Button>
      </Paper>
    </Container>
    </>
  );
};

export default Welcome;
